/**
 * Empty state canonique à pastille (INSTR / CLI / LRN…) : icône dans une pastille,
 * titre, sous-titre, et CTA optionnel. Source de vérité UNIQUE pour éviter la
 * dérive entre onglets (cf. convention_empty_state_pastille_color).
 *
 * Porte AUSSI la forme d'ERREUR (`tone="error"`), pour la même raison : trois
 * écrans de la page Satisfaction rendaient leur échec de trois façons
 * différentes — pastille d'accueil ici, `<div>` bordé là, `Alert` destructive
 * ailleurs — sur une seule et même page (constat 2026-08-26).
 */

import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface Props {
    icon: LucideIcon
    title: string
    description: string
    /** Bouton d'action (CTA) — omis en lecture seule. */
    action?: ReactNode
    /**
     * `error` pour un ÉCHEC, `empty` (défaut) pour une absence de matière.
     *
     * ⚠️ Les deux ne se confondent pas : « rien à montrer » et « je n'ai pas pu
     * regarder » appellent des gestes opposés. Rendre une erreur dans la pastille
     * d'accueil la fait lire comme un vide — c'est ce que faisait l'écran
     * « Résultats » de la satisfaction, avec une icône de graphique.
     */
    tone?: 'empty' | 'error'
    /**
     * Ajustement d'espacement LOCAL (ex. `pt-6` sous une barre d'action qui porte
     * déjà le geste). Sans lui, rien ne change pour les autres écrans.
     */
    className?: string
}

export function EmptyStateCTA({
    icon: Icon,
    title,
    description,
    action,
    tone = 'empty',
    className,
}: Props) {
    const erreur = tone === 'error'
    return (
        <div className={cn('flex flex-col items-center justify-center gap-3 py-16 text-center', className)}>
            <div
                className={`flex h-12 w-12 items-center justify-center rounded-full ${
                    erreur ? 'bg-destructive/10' : 'bg-primary/10'
                }`}
            >
                <Icon className={`h-6 w-6 ${erreur ? 'text-destructive' : 'text-primary'}`} />
            </div>
            <div>
                <p className="text-sm font-medium text-foreground">{title}</p>
                <p className="mt-1 max-w-md text-xs text-muted-foreground">{description}</p>
            </div>
            {action}
        </div>
    )
}

export default EmptyStateCTA
